import { Compass, Crosshair, FileWarning, Lock, PlayCircle, Sparkle, Waypoints } from "lucide-react";
import { Rule, roman } from "./Ornament";
import { WaxSeal } from "./WaxSeal";
import { domainLabel, listWords, nodeById } from "@/lib/catalog";

const KindChip = ({ essential, long }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[0.66rem] font-semibold font-mono uppercase tracking-[0.16em]"
    style={{
      color: `hsl(var(--${essential ? "hard" : "soft"}))`,
      background: `hsl(var(--${essential ? "hard-bg" : "soft-bg"}))`,
      borderColor: `hsl(var(--${essential ? "hard" : "soft"}) / 0.35)`,
    }}
  >
    {essential ? <Lock className="h-3 w-3" /> : <Sparkle className="h-3 w-3" />}
    {essential ? "Essential" : "Recommended"}
  </span>
);

export const DraftBanner = ({ compact }) => (
  <div data-testid="draft-banner" className={`flex items-start gap-3 rounded-lg border border-dashed border-destructive/50 bg-destructive/10 ${compact ? "p-3" : "p-4"}`}>
    <FileWarning className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
    <div>
      <span className="eyebrow text-destructive">Draft · AI-generated · not in the Atlas catalog</span>
      {!compact && (
        <p className="text-sm text-muted-foreground mt-1">
          This route was sketched by an AI because no catalog node matched. It has no placement check and is not a demo of Atlas routing.
        </p>
      )}
    </div>
  </div>
);

const DiagnosticCta = ({ route, probe, onTakeDiagnostic }) => {
  const domains = listWords(probe.domains.map(domainLabel));
  const destCats = [...new Set(route.targets.map((id) => nodeById[id]?.cat))];
  const destInstrumented = destCats.some((c) => probe.domains.includes(c));
  const destName = destCats.map((c) => domainLabel(c)).join(" / ");
  return (
    <>
      <p className="mt-2 text-base text-foreground leading-relaxed">
        {destInstrumented
          ? `The ${domains} steps on this path are instrumented. Atlas can find where you’re standing on them (${probe.count} probes).`
          : `This path walks through ${domains}. Atlas can check those steps before you start.`}
      </p>
      <p data-testid="diagnostic-subcopy" className="mt-1.5 text-sm text-muted-foreground">
        {destInstrumented
          ? "One probe per instrumented step. It places you; it does not grade you."
          : `There is no ${destName} placement yet. Atlas can only test the instrumented steps.`}
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <button data-testid="placement-check-trigger-button" onClick={onTakeDiagnostic} className="btn-brass">
          <Crosshair className="h-4 w-4" /> Check the {domains} on this path
        </button>
      </div>
    </>
  );
};

export const RouteResult = ({ route, sealed, hints, probe, onTakeDiagnostic, onStartFirst, onExplore }) => {
  const steps = route.steps || [];
  const first = steps[0];
  const sealedCount = steps.filter((_, i) => sealed?.has(i)).length;
  const firstUnsealed = steps.findIndex((_, i) => !sealed?.has(i));
  const resumeIndex = firstUnsealed === -1 ? 0 : firstUnsealed;
  const showDiag = !route.draft && probe?.count > 0;
  const essentialCount = route.counts?.essential ?? steps.filter((s) => s.essential).length;
  const probeIds = new Set(probe?.ids || []);
  const instrumented = steps.filter((s) => probeIds.has(s.id)).length;

  return (
    <section data-testid="route-result-state" className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-20 atlas-rise">
      {route.draft && <div className="mb-8"><DraftBanner /></div>}

      <div data-testid="route-summary-header" className="mb-10">
        <Rule label={route.draft ? "Draft route to" : "Your route to"} className="max-w-xs mb-5" />
        <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-center">
          {route.destination}
        </h2>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed text-center max-w-2xl mx-auto">{route.summary}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="numeral text-primary text-lg">{roman(steps.length)}</span> topics on the route
          </span>
          <span className="inline-flex items-center gap-2"><KindChip essential long /> {essentialCount} — cannot proceed without</span>
          <span className="inline-flex items-center gap-2"><KindChip essential={false} long /> easier with</span>
          {!route.draft && (
            <span
              data-testid="probe-coverage-badge"
              title="Steps with an authored placement probe"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 ${instrumented ? "border-primary/40 text-primary" : "border-border text-muted-foreground"}`}
            >
              <Crosshair className="h-3 w-3" />
              <span className="numeral">{roman(instrumented) === "0" ? "0" : roman(instrumented)}</span> of <span className="numeral">{roman(steps.length)}</span> steps instrumented
            </span>
          )}
          {sealedCount > 0 && (
            <span data-testid="route-seal-progress" className="inline-flex items-center gap-1.5">
              <WaxSeal className="h-5 w-5" />
              <span className="numeral text-primary">{roman(sealedCount)}</span> of <span className="numeral">{roman(steps.length)}</span> sealed
            </span>
          )}
        </div>
        {!route.draft && (
          <p className="mt-4 text-center text-xs text-muted-foreground font-display italic">
            A suggested order, not a gate — traced from the prerequisite graph.
          </p>
        )}
      </div>

      <ol data-testid="route-step-list" className="relative space-y-3">
        <span className="absolute left-[19px] top-4 bottom-4 w-px bg-gradient-to-b from-primary/10 via-primary/50 to-primary/10" aria-hidden />
        {steps.map((s, i) => {
          const goal = s.is_goal ?? i === steps.length - 1;
          const isSealed = sealed?.has(i);
          const isHint = !isSealed && hints?.has(s.id);
          return (
            <li
              key={s.id || i}
              data-testid={`route-step-item-${i}`}
              data-sealed={isSealed ? "true" : "false"}
              data-hint={isHint ? "true" : "false"}
              className={`card-ink relative flex gap-4 p-4 sm:p-5 transition-colors hover:border-primary/40 ${isSealed ? "border-primary/40 bg-accent/30" : ""}`}
            >
              {isSealed ? (
                <WaxSeal className="relative z-10 h-10 w-10 shrink-0 seal-pop" label={`Step ${i + 1} sealed`} />
              ) : (
                <span className={`relative z-10 grid place-items-center h-10 w-10 shrink-0 rounded-full numeral text-sm ${goal ? "bg-primary text-primary-foreground" : "bg-background text-primary border border-primary/50"}`}>
                  {goal ? <Compass className="h-5 w-5" strokeWidth={1.7} /> : roman(i + 1)}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="font-display font-medium text-xl leading-tight">{s.title}</h3>
                  {isSealed ? <span className="eyebrow text-primary">Sealed</span> : goal ? <span className="eyebrow text-primary">Destination</span> : <KindChip essential={s.essential ?? s.kind === "hard"} />}
                  {probeIds.has(s.id) && (
                    <span data-testid="step-probe-badge" title="Has a placement probe" className="inline-flex items-center gap-1 eyebrow text-muted-foreground">
                      <Crosshair className="h-3 w-3 text-primary" /> probe
                    </span>
                  )}
                  {isHint && <span data-testid="hint-chip" className="eyebrow text-[hsl(var(--soft))]">Looks solid · worth a quick check</span>}
                  {route.draft && <span className="eyebrow text-destructive">Draft</span>}
                </div>
                <p className="mt-1.5 text-sm sm:text-base text-muted-foreground leading-relaxed">{s.detail}</p>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="plate-frame mt-10 card-ink bg-secondary/40 p-6 sm:p-7">
        <span className="eyebrow text-primary">What happens next</span>
        {sealedCount > 0 ? (
          <>
            <p className="mt-2 text-base text-foreground leading-relaxed">
              {sealedCount === steps.length
                ? "Every step on this route carries your seal. Revisit any step, or chart a new destination."
                : `You’ve sealed ${roman(sealedCount)} of ${roman(steps.length)} steps. Pick up where the wax is still warm.`}
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button data-testid="primary-next-step-cta" onClick={() => onStartFirst(resumeIndex)} className="btn-brass">
                <PlayCircle className="h-4 w-4" />
                {sealedCount === steps.length ? "Revisit from step I" : `Continue at step ${roman(resumeIndex + 1)}`}
              </button>
              {showDiag && (
                <button data-testid="placement-check-trigger-button" onClick={onTakeDiagnostic} className="btn-ghost">
                  <Crosshair className="h-4 w-4" /> Re-check the {listWords(probe.domains.map(domainLabel))}
                </button>
              )}
            </div>
          </>
        ) : showDiag ? (
          <>
            <DiagnosticCta route={route} probe={probe} onTakeDiagnostic={onTakeDiagnostic} />
            <button data-testid="skip-to-first-topic-button" onClick={() => onStartFirst(0)} className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
              <PlayCircle className="h-4 w-4" /> Skip the check &amp; start at step I
            </button>
          </>
        ) : (
          <>
            <p className="mt-2 text-base text-foreground leading-relaxed">
              {route.draft
                ? "Draft routes have no placement check. Begin with the first topic and treat the order as a sketch."
                : "No instrumented steps on this path yet, so there is no placement check. Begin with the first topic."}
            </p>
            <div className="mt-5">
              <button data-testid="primary-next-step-cta" onClick={() => onStartFirst(0)} className="btn-brass">
                <PlayCircle className="h-4 w-4" /> Start with “{first?.title}”
              </button>
            </div>
          </>
        )}
      </div>

      <div className="mt-10 flex items-center justify-center">
        <button data-testid="explore-web-secondary-cta" onClick={onExplore} className="group inline-flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <span className="grid place-items-center h-8 w-8 rounded-full border border-border bg-card/60 group-hover:border-primary/60 transition-colors">
            <Waypoints className="h-4 w-4 text-primary" strokeWidth={1.7} />
          </span>
          <span className="font-display italic">Explore how this route connects on the knowledge web</span>
        </button>
      </div>
    </section>
  );
};
